/**
 * /ayarlar kaydetme mantigi — sozlesme §5 PUT /api/v2/tenant/settings
 * yalniz DEGISEN ust-duzey noktali anahtarlari kabul eder ("kaydet = PUT
 * {values} yalnız değişen anahtarlar", gorev talimati madde 2).
 *
 * `schedule.depo`/`schedule.kargo` (6 zorunlu alan) ve `sync.intervals` gibi
 * ic ice degerler icin backend TUM nesneyi bekler (settings.py
 * `_validate_schedule_obj`/`_validate_sync_intervals`) — bu yuzden fark
 * ANAHTAR bazinda alinir (JSON esitligi), degisen bir anahtarin degeri HER
 * ZAMAN butunuyle gonderilir; ic ice tek bir alanin "parca parca" gonderimi
 * YOKTUR.
 */
export function buildChangedSettingsValues(
  original: Record<string, unknown>,
  draft: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  for (const key of keys) {
    const before = original[key];
    const after = draft[key];
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      changed[key] = after;
    }
  }
  return changed;
}

/** "422 alan hatasi": backend `validate_settings`/`_validate_one` HER ZAMAN
 * tek bir string firlatir, "<anahtar>: <mesaj>" bicimli (settings.py). Ilk
 * ": " once anahtar, sonrasi (dahil, orijinal mesaj) kullanicidan
 * saklanmaz — hem alanin altina hem anahtara gore sekme/alan vurgusuna
 * kullanilir. Eslesen bilinen anahtar yoksa (ör. "bilinmeyen ayar anahtari:
 * X" gibi anahtarsiz mesajlar) field null doner — cagiran genel bir hata
 * banner'i gosterir. */
export function parseFieldError(
  message: string,
  knownKeys: readonly string[],
): { field: string | null; message: string } {
  const idx = message.indexOf(": ");
  if (idx === -1) return { field: null, message };
  const candidate = message.slice(0, idx);
  if (knownKeys.includes(candidate)) return { field: candidate, message };
  // Ic ice anahtar (inceleme bulgusu): settings.py `_validate_schedule_obj`/
  // `_validate_sync_intervals` "<ust_anahtar>.<alt_alan>: ..." bicimli hata
  // firlatir (ör. "schedule.depo.saturday_works: bool bekleniyor",
  // "sync.intervals.orders: pozitif tamsayı bekleniyor") — bunlar dogrudan
  // knownKeys'te YOKTUR, bu yuzden sekme/alan vurgusu hic calismiyordu. En
  // uzun eslesen ust-duzey anahtar (candidate bununla "." + devam ediyorsa)
  // sekme/alan vurgusu icin kullanilir; goruntulenen mesaj DEGISMEZ.
  const nested = knownKeys
    .filter((key) => candidate.startsWith(`${key}.`))
    .sort((a, b) => b.length - a.length)[0];
  if (nested) return { field: nested, message };
  return { field: null, message };
}

/** Gizli kart/sekme cok-secim listeleri icin: canli /cards yanitindan
 * gelenler + HALIHAZIRDA gizli olup /cards yanitinda hic GORUNMEYEN
 * (compute_states "hidden_cards icindeki kartlar sonuca hic girmez" —
 * capabilities.py) id'ler BIRLESTIRILIR, aksi halde bir karti gizledikten
 * sonra listeden dusup bir daha ASLA geri acilamaz hale gelirdi. */
export function buildToggleOptions(
  known: Array<{ id: string; title: string }>,
  currentlyHidden: readonly string[],
): Array<{ id: string; title: string }> {
  const byId = new Map(known.map((item) => [item.id, item] as const));
  for (const id of currentlyHidden) {
    if (!byId.has(id)) byId.set(id, { id, title: id });
  }
  return Array.from(byId.values());
}
